import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarOutlined
} from '@ant-design/icons';
import CommonService from '../../../services/common.service';
import { toast } from 'react-toastify';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { API_URLS } from '../../../constants/api-urls';
import '../../../assets/styles/seller_quotation_list.scss';

export default function SellerQuotationList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedRFQs, setExpandedRFQs] = useState({});
  const [hoveredQuotationId, setHoveredQuotationId] = useState(null);
  const [hoveredRejectReasonId, setHoveredRejectReasonId] = useState(null);
  const [showRejectReasonModal, setShowRejectReasonModal] = useState(false);
  const [selectedRejectQuotation, setSelectedRejectQuotation] = useState(null);
  const [showAcceptanceDetailsModal, setShowAcceptanceDetailsModal] = useState(false);
  const [selectedAcceptQuotation, setSelectedAcceptQuotation] = useState(null);
  const [showQualityInspectorModal, setShowQualityInspectorModal] = useState(false);
  const [selectedQuotationForQA, setSelectedQuotationForQA] = useState(null);
  const [quotations, setQuotations] = useState({ pending: [], accepted: [], rejected: [] });
  const [loading, setLoading] = useState(true);
  
  // Track QA request drafts: quotation_id -> {qaRequestId, status}
  const [qaRequestDrafts, setQaRequestDrafts] = useState({});
  // Track submitted (non-draft) QA requests so we don't show "Quality Inspector Evaluation" again
  const [submittedQARequests, setSubmittedQARequests] = useState({});

  // Master Data Hooks
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();
  const { attributeUnitOptions, loading: uomLoading } = useProductDetailsMaster();
  
  // Store raw quotations data for re-transformation
  const [rawQuotations, setRawQuotations] = useState([]);

  // Helper function to get currency label from value (ID or code)
  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) return currencyValue;
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) || 
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    return currencyValue;
  };

  // Helper function to get currency symbol from currency code (for view-only displays)
  // Uses only API data - no hardcoded fallbacks
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    
    // Get symbol from currencyOptions (API data only)
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    
    // Return empty string if no symbol found from API
    return '';
  };

  // Helper function to get unit label from value (ID or label)
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return 'Units';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const foundByValue = attributeUnitOptions.find(opt => 
      String(opt.value) === String(unitValue)
    );
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue)
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Transform raw quotations data using master data
  const transformQuotations = (apiQuotations) => {
    if (!apiQuotations || apiQuotations.length === 0) {
      return { pending: [], accepted: [], rejected: [] };
    }
    
    // Transform API data to match UI structure
    const transformedQuotations = apiQuotations.map(quotation => {
            // Get RFQ number
            const rfqNumber = quotation.rfq_id ? `REQ-${quotation.rfq_id}` : 'N/A';
            
            // Get quotation ID
            const quotationId = quotation.quotation_number || `QUO-${quotation.quotation_id}`;
            
            // Preserve quotation_id and rfq_id in transformed object for easy access
            const preservedQuotationId = quotation.quotation_id;
            const preservedRfqId = quotation.rfq_id;
            
            // Get status (map to UI statuses)
            // Database values: 'Draft', 'Submitted', 'Accepted', 'Rejected', 'Expired'
            // UI values: 'Pending', 'Accepted', 'Rejected'
            const quoteStatus = quotation.quote_status ? String(quotation.quote_status).trim() : '';
            let status = 'Pending';
            
            if (quoteStatus.toLowerCase() === 'accepted') {
              status = 'Accepted';
            } else if (quoteStatus.toLowerCase() === 'rejected') {
              status = 'Rejected';
            } else {
              // 'Draft', 'Submitted', 'Expired', or any other status maps to 'Pending'
              status = 'Pending';
            }
            
            // Get supplier name
            const supplier = quotation.seller_company_name || quotation.seller_name || 'Unknown Supplier';
            
            // Get submitted date
            const submittedDate = quotation.created_at 
              ? new Date(quotation.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : quotation.quote_date 
                ? new Date(quotation.quote_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                : 'N/A';
            
            // Get valid until date
            let validUntil = 'N/A';
            if (quotation.quote_date && quotation.validity_days) {
              const quoteDate = new Date(quotation.quote_date);
              quoteDate.setDate(quoteDate.getDate() + parseInt(quotation.validity_days));
              validUntil = quoteDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            
            // Get line items
            const lineItems = quotation.items || [];
            const itemsCount = lineItems.length;
            
            // Get first product for display (or aggregate if multiple)
            let productName = 'Multiple Products';
            let quantity = 0;
            let unit = 'Units';
            let unitPrice = 0;
            let totalAmount = 0;
            
            if (lineItems.length > 0) {
              if (lineItems.length === 1) {
                const item = lineItems[0];
                productName = item.description || 'Product';
                quantity = parseFloat(item.quantity) || 0;
                unit = getUnitLabel(item.uom) || 'Units';
                unitPrice = parseFloat(item.unit_price) || 0;
                totalAmount = parseFloat(item.total) || 0;
              } else {
                // Multiple items - aggregate
                quantity = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
                totalAmount = lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
                unitPrice = quantity > 0 ? totalAmount / quantity : 0;
              }
            }
            
            // Get currency
            const currencyCode = getCurrencyLabel(quotation.currency || 'INR');
            const currencySymbol = getCurrencySymbol(currencyCode);
            
            return {
              id: quotationId,
              status: status,
              supplier: supplier,
              itemsCount: itemsCount,
              submittedDate: submittedDate,
              rfqNumber: rfqNumber,
              validUntil: validUntil,
              totalAmount: totalAmount,
              currency: currencyCode,
              currencySymbol: currencySymbol,
              products: lineItems.map(item => ({
                name: item.description || 'Product',
                quantity: parseFloat(item.quantity) || 0,
                unit: getUnitLabel(item.uom) || 'Units',
                unitPrice: parseFloat(item.unit_price) || 0
              })),
              bulkPricing: lineItems.flatMap(item => {
                if (item.bulk_pricing && Array.isArray(item.bulk_pricing) && item.bulk_pricing.length > 0) {
                  return item.bulk_pricing.map(tier => ({
                    minQty: tier.minQty || tier.min_qty || 0,
                    maxQty: tier.maxQty || tier.max_qty || null,
                    price: parseFloat(tier.unitPrice || tier.unit_price || 0),
                    discount: `${parseFloat(tier.discount || 0)}%`
                  }));
                }
                return [];
              }),
              rejectReason: quotation.rejection_reason || quotation.reject_reason || null,
              rejectRemarks: quotation.rejection_remarks || null,
              acceptanceNotes: quotation.acceptance_notes || null,
              acceptanceAttachment: quotation.acceptance_attachment || null,
              quotationData: quotation, // Store full quotation data for navigation
              quotation_id: preservedQuotationId, // Preserve quotation_id for easy access
              rfq_id: preservedRfqId // Preserve rfq_id for easy access
            };
    });
    
    // Group by status
    const grouped = {
      pending: transformedQuotations.filter(q => q.status === 'Pending'),
      accepted: transformedQuotations.filter(q => q.status === 'Accepted'),
      rejected: transformedQuotations.filter(q => q.status === 'Rejected')
    };
    
    return grouped;
  };
  
  // Fetch QA request drafts for accepted quotations
  const fetchQARequestDrafts = useCallback(async (apiQuotations) => {
    try {
      // Filter accepted quotations
      const acceptedQuotations = apiQuotations.filter(q => 
        q.quote_status && q.quote_status.toLowerCase() === 'accepted'
      );
      
      if (acceptedQuotations.length === 0) {
        return;
      }
      
      // Fetch QA request status for each accepted quotation
      const draftPromises = acceptedQuotations.map(async (quotation) => {
        try {
          const response = await CommonService.get(API_URLS.QA.QUOTATION_BY_ID(quotation.quotation_id));
          if (response && response.success && response.data) {
            const qaRequest = response.data;
            return {
              quotationId: quotation.quotation_id,
              qaRequestId: qaRequest.qa_request_id,
              status: qaRequest.status || 'Draft'
            };
          }
        } catch (error) {
          // QA request doesn't exist or error fetching - ignore
          return null;
        }
        return null;
      });
      
      const draftResults = await Promise.all(draftPromises);
      const draftsMap = {};
      const submittedMap = {};
      
      draftResults.forEach(result => {
        if (!result) return;
        const key = Number(result.quotationId);
        if (result.status === 'Draft') {
          draftsMap[key] = {
            qaRequestId: result.qaRequestId,
            status: result.status
          };
        } else {
          // Already submitted (Submitted, Broadcast, etc.) – don't show Quality Inspector Evaluation again
          submittedMap[key] = {
            qaRequestId: result.qaRequestId,
            status: result.status
          };
        }
      });
      
      setQaRequestDrafts(draftsMap);
      setSubmittedQARequests(submittedMap);
    } catch (error) {
      console.error('Error fetching QA request drafts:', error);
      // Don't show error to user, just log it
    }
  }, []);
  
  // Helper function to prepare scope_logistics for API (same as buyer_qa_request.js)
  const prepareScopeLogistics = (scopeData) => {
    if (!scopeData || Object.keys(scopeData).length === 0) {
      return {};
    }
    
    const prepared = { ...scopeData };
    
    // Convert inspection_category to string
    if (prepared.inspection_category !== undefined && prepared.inspection_category !== null && prepared.inspection_category !== '') {
      prepared.inspection_category = String(prepared.inspection_category);
    } else if (prepared.inspection_category === undefined || prepared.inspection_category === null) {
      prepared.inspection_category = '';
    }
    
    // Convert inspection_type array to JSON string (validator expects string)
    if (Array.isArray(prepared.inspection_type)) {
      prepared.inspection_type = JSON.stringify(prepared.inspection_type);
    } else if (prepared.inspection_type === undefined || prepared.inspection_type === null) {
      prepared.inspection_type = '';
    }
    
    // Ensure keywords_tags and technical_drawings_sops are arrays
    if (!Array.isArray(prepared.keywords_tags)) {
      prepared.keywords_tags = [];
    }
    if (!Array.isArray(prepared.technical_drawings_sops)) {
      prepared.technical_drawings_sops = [];
    }
    
    // Handle email field
    if (prepared.email === undefined || prepared.email === null) {
      delete prepared.email;
    } else if (prepared.email === '') {
      prepared.email = '';
    }
    
    // Convert country, state, city to strings
    if (prepared.country !== undefined && prepared.country !== null && prepared.country !== '') {
      prepared.country = String(prepared.country);
    } else if (prepared.country === undefined || prepared.country === null) {
      prepared.country = '';
    }
    if (prepared.state !== undefined && prepared.state !== null && prepared.state !== '') {
      prepared.state = String(prepared.state);
    } else if (prepared.state === undefined || prepared.state === null) {
      prepared.state = '';
    }
    if (prepared.city !== undefined && prepared.city !== null && prepared.city !== '') {
      prepared.city = String(prepared.city);
    } else if (prepared.city === undefined || prepared.city === null) {
      prepared.city = '';
    }
    
    // Convert other numeric fields to strings
    if (prepared.lot_quantity !== undefined && prepared.lot_quantity !== null && prepared.lot_quantity !== '') {
      prepared.lot_quantity = String(prepared.lot_quantity);
    } else if (prepared.lot_quantity === undefined || prepared.lot_quantity === null) {
      prepared.lot_quantity = '';
    }
    if (prepared.pincode !== undefined && prepared.pincode !== null && prepared.pincode !== '') {
      prepared.pincode = String(prepared.pincode);
    } else if (prepared.pincode === undefined || prepared.pincode === null) {
      prepared.pincode = '';
    }
    if (prepared.phone !== undefined && prepared.phone !== null && prepared.phone !== '') {
      prepared.phone = String(prepared.phone);
    } else if (prepared.phone === undefined || prepared.phone === null) {
      prepared.phone = '';
    }
    
    return prepared;
  };
  
  // Helper function to prepare compliance_commercials for API
  const prepareComplianceCommercials = (complianceData) => {
    if (!complianceData || Object.keys(complianceData).length === 0) {
      return {};
    }
    
    const prepared = { ...complianceData };
    
    // Ensure proof_of_insurance is an array
    if (!Array.isArray(prepared.proof_of_insurance)) {
      prepared.proof_of_insurance = [];
    }
    
    return prepared;
  };
  
  // Handle submitting QA request - only redirect to buyer-qa-request page
  const handleSubmitQARequest = (quotation) => {
    const quotationId = quotation.quotation_id || quotation.quotationData?.quotation_id;
    const rfqId = quotation.rfq_id || quotation.quotationData?.rfq_id;
    
    if (!quotationId || !rfqId) {
      toast.error('Missing quotation or RFQ information');
      return;
    }
    
    // Navigate to buyer-qa-request page
    navigate(`/buyer-qa-request?quotation_id=${quotationId}&rfq_id=${rfqId}`);
  };

  // Set active tab from location state (when navigating from accept/reject)
  useEffect(() => {
    if (location.state?.activeTab) {
      const tab = location.state.activeTab;
      if (['pending', 'accepted', 'rejected'].includes(tab)) {
        setActiveTab(tab);
      }
    }
  }, [location.state]);

  // Fetch quotations from API (on mount and when location state changes)
  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        setLoading(true);
        const response = await CommonService.get('/quotation/buyer/my-quotations');
        
        if (response && response.success && response.data) {
          const apiQuotations = Array.isArray(response.data) ? response.data : [];
          
          // Debug: Log status distribution
          const statusCounts = apiQuotations.reduce((acc, q) => {
            const status = q.quote_status || 'Unknown';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});
          console.log('📊 Quotation Status Distribution:', statusCounts);
          
          // Debug: Log rejection data for rejected quotations
          const rejectedQuotations = apiQuotations.filter(q => 
            q.quote_status && q.quote_status.toLowerCase() === 'rejected'
          );
          if (rejectedQuotations.length > 0) {
            console.log('🔍 Rejected Quotations Debug:', rejectedQuotations.map(q => ({
              quotation_id: q.quotation_id,
              rejection_reason: q.rejection_reason,
              rejection_remarks: q.rejection_remarks,
              hasRejectionReason: !!q.rejection_reason,
              hasRejectionRemarks: !!q.rejection_remarks,
              allKeys: Object.keys(q).filter(key => key.includes('reject') || key.includes('Reject'))
            })));
          }
          
          setRawQuotations(apiQuotations);
        } else {
          setRawQuotations([]);
        }
      } catch (error) {
        console.error('Error fetching quotations:', error);
        
        // Handle 401 Unauthorized - redirect to login
        if (error?.response?.status === 401) {
          toast.error('Your session has expired. Please login again.');
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
          return;
        }
        
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch quotations';
        toast.error(errorMessage);
        setRawQuotations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, [location.state?.activeTab]); // Re-fetch when navigating back from accept/reject

  // Use ref to track if we've already transformed to prevent infinite loops
  const hasTransformedRef = useRef(false);
  const lastRawQuotationsLengthRef = useRef(0);
  const lastCurrencyOptionsLengthRef = useRef(0);
  const lastAttributeUnitOptionsLengthRef = useRef(0);
  const lastRawQuotationsHashRef = useRef('');

  // Transform quotations when master data is available
  useEffect(() => {
    const rawQuotationsLength = rawQuotations.length;
    const currencyOptionsLength = currencyOptions?.length || 0;
    const attributeUnitOptionsLength = attributeUnitOptions?.length || 0;
    
    // Create a hash of raw quotations to detect content changes (not just length)
    // This includes quotation IDs and statuses to detect when status changes
    const quotationsHash = JSON.stringify(
      rawQuotations.map(q => ({
        id: q.quotation_id,
        status: q.quote_status
      })).sort((a, b) => (a.id || 0) - (b.id || 0))
    );
    
    // Check if any relevant data has changed
    const dataChanged = rawQuotationsLength !== lastRawQuotationsLengthRef.current ||
                       currencyOptionsLength !== lastCurrencyOptionsLengthRef.current ||
                       attributeUnitOptionsLength !== lastAttributeUnitOptionsLengthRef.current ||
                       quotationsHash !== lastRawQuotationsHashRef.current;
    
    // Only transform if:
    // 1. We have raw data
    // 2. Master data is loaded
    // 3. Data has actually changed
    const shouldTransform = rawQuotationsLength > 0 && 
                          !currencyLoading && 
                          !uomLoading && 
                          currencyOptionsLength > 0 && 
                          attributeUnitOptionsLength > 0 &&
                          (dataChanged || !hasTransformedRef.current);

    if (shouldTransform) {
      const transformed = transformQuotations(rawQuotations);
      
      // Debug: Log transformed status distribution
      console.log('📊 Transformed Status Distribution:', {
        pending: transformed.pending.length,
        accepted: transformed.accepted.length,
        rejected: transformed.rejected.length,
        total: rawQuotationsLength
      });
      
      setQuotations(transformed);
      hasTransformedRef.current = true;
      lastRawQuotationsLengthRef.current = rawQuotationsLength;
      lastCurrencyOptionsLengthRef.current = currencyOptionsLength;
      lastAttributeUnitOptionsLengthRef.current = attributeUnitOptionsLength;
      lastRawQuotationsHashRef.current = quotationsHash;
      
      // Fetch QA request drafts for accepted quotations
      fetchQARequestDrafts(rawQuotations);
    } else if (rawQuotationsLength === 0 && !loading) {
      setQuotations({ pending: [], accepted: [], rejected: [] });
      hasTransformedRef.current = false;
      lastRawQuotationsLengthRef.current = 0;
      lastRawQuotationsHashRef.current = '';
      // Clear drafts when no quotations
      setQaRequestDrafts({});
    }
  }, [rawQuotations, currencyLoading, uomLoading, loading, currencyOptions, attributeUnitOptions]);
  
  // Refetch QA request drafts when activeTab changes to 'accepted' or when component mounts
  useEffect(() => {
    if (activeTab === 'accepted' && rawQuotations.length > 0) {
      fetchQARequestDrafts(rawQuotations);
    }
  }, [activeTab, rawQuotations, fetchQARequestDrafts]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const formatCurrency = (amount, currencyCode = 'INR', currencySymbol = '') => {
    if (!amount && amount !== 0) return '-';
    const symbol = currencySymbol || getCurrencySymbol(currencyCode);
    return `${symbol}${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  // Get bulk pricing data from quotation
  const getBulkPricingData = (quotation) => {
    if (quotation.bulkPricing && quotation.bulkPricing.length > 0) {
      return quotation.bulkPricing;
    }
    return [];
  };

  // Group quotations by RFQ number
  const groupQuotationsByRFQ = (quotationsList) => {
    const grouped = {};
    quotationsList.forEach((quotation) => {
      const rfqNumber = quotation.rfqNumber;
      if (!grouped[rfqNumber]) {
        grouped[rfqNumber] = [];
      }
      grouped[rfqNumber].push(quotation);
    });
    return grouped;
  };

  const currentQuotations = quotations[activeTab] || [];
  const groupedByRFQ = groupQuotationsByRFQ(currentQuotations);
  const rfqNumbers = Object.keys(groupedByRFQ).sort();

  // Toggle RFQ expand/collapse
  const toggleRFQ = (rfqNumber) => {
    setExpandedRFQs((prev) => ({
      ...prev,
      [rfqNumber]: !prev[rfqNumber]
    }));
  };

  // Initialize all RFQs as collapsed by default
  React.useEffect(() => {
    const allCollapsed = {};
    rfqNumbers.forEach((rfqNumber) => {
      allCollapsed[rfqNumber] = false;
    });
    setExpandedRFQs((prev) => ({ ...allCollapsed, ...prev }));
  }, [activeTab, rfqNumbers.length]);

  return (
    <div className="seller-quotation-list">
      {/* Main Content */}
      <div className="sql-main-content">
        <div className="sql-title-section">
          <h1 className="sql-main-title">Quotations Management</h1>
          <p className="sql-subtitle">Review and manage all received quotations</p>
        </div>

        {/* Tabs */}
        <div className="sql-tabs">
          <button
            className={`sql-tab ${activeTab === 'pending' ? 'sql-tab-active' : ''}`}
            onClick={() => handleTabChange('pending')}
          >
            Pending ({quotations.pending.length})
          </button>
          <button
            className={`sql-tab ${activeTab === 'accepted' ? 'sql-tab-active' : ''}`}
            onClick={() => handleTabChange('accepted')}
          >
            Accepted ({quotations.accepted.length})
          </button>
          <button
            className={`sql-tab ${activeTab === 'rejected' ? 'sql-tab-active' : ''}`}
            onClick={() => handleTabChange('rejected')}
          >
            Rejected ({quotations.rejected.length})
          </button>
        </div>

        {/* Quotation Cards - Grouped by RFQ */}
        <div className="sql-quotations-container">
          {rfqNumbers.length > 0 ? (
            rfqNumbers.map((rfqNumber) => {
              const rfqQuotations = groupedByRFQ[rfqNumber];
              const suppliersCount = rfqQuotations.length;
              
              const isExpanded = expandedRFQs[rfqNumber] === true;

              return (
                <div key={rfqNumber} className="sql-rfq-group">
                  <div 
                    className="sql-rfq-header" 
                    onClick={() => toggleRFQ(rfqNumber)}
                  >
                    <div className="sql-rfq-header-left">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="sql-rfq-icon"
                        aria-hidden="true"
                      >
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                        <path d="M10 9H8"></path>
                        <path d="M16 13H8"></path>
                        <path d="M16 17H8"></path>
                      </svg>
                      <div className="sql-rfq-info">
                        <div className="sql-rfq-number">{rfqNumber}</div>
                        <div className="sql-rfq-suppliers-count">
                          {suppliersCount} {suppliersCount === 1 ? 'supplier' : 'suppliers'} responded
                        </div>
                      </div>
                    </div>
                    <div className="sql-rfq-header-right">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        className="sql-rfq-chevron"
                        aria-hidden="true"
                        style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
                      >
                        <path d="m18 15-6-6-6 6"></path>
                      </svg>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="sql-rfq-quotations">
                      {rfqQuotations.map((quotation) => (
                      <div 
                        key={quotation.id} 
                        className="sql-quotation-card"
                        onClick={() => navigate('/seller-quotation-accept', { state: { quotation: quotation.quotationData, status: quotation.status } })}
                      >
                        <div className="sql-card-content">
                          <div className="sql-card-header">
                            <div className="sql-quotation-id">{quotation.id}</div>
                            <div className={`sql-status-badge sql-status-${quotation.status.toLowerCase()}`}>
                              {quotation.status}
                            </div>
                          </div>

                          <div className={`sql-card-body ${activeTab === 'rejected' ? 'sql-rejected-tab' : ''} ${activeTab === 'accepted' ? 'sql-accepted-tab' : ''}`}>
                            <div className="sql-info-column sql-supplier-column">
                              <div className="sql-info-label">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="14" 
                                  height="14" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  className="sql-info-icon"
                                  aria-hidden="true"
                                >
                                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                                  <path d="M10 6h4"></path>
                                  <path d="M10 10h4"></path>
                                  <path d="M10 14h4"></path>
                                  <path d="M10 18h4"></path>
                                </svg>
                                Supplier
                              </div>
                              <div className="sql-info-value">{quotation.supplier}</div>
                              <div className="sql-info-meta">
                                {quotation.itemsCount} item(s) • Submitted: {quotation.submittedDate}
                              </div>
                            </div>

                            {/* Products Information */}
                            <div className="sql-info-column sql-products-column">
                              <div className="sql-info-label">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="14" 
                                  height="14" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  className="sql-info-icon"
                                  aria-hidden="true"
                                >
                                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                                  <path d="M3 6h18"></path>
                                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                                </svg>
                                Products
                              </div>
                              {quotation.products && quotation.products.length > 0 ? (
                                <div className="sql-info-value">{quotation.products[0].name}</div>
                              ) : (
                                <div className="sql-info-value">No products</div>
                              )}
                            </div>

                            {/* Quantity */}
                            <div className="sql-info-column">
                              <div className="sql-info-label">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="14" 
                                  height="14" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  className="sql-info-icon"
                                  aria-hidden="true"
                                >
                                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                                </svg>
                                Quantity
                              </div>
                              {quotation.products && quotation.products.length > 0 ? (
                                <div className="sql-info-value">
                                  {quotation.products[0].quantity} {quotation.products[0].unit || 'Units'}
                                </div>
                              ) : (
                                <div className="sql-info-value">-</div>
                              )}
                            </div>

                            {/* Unit Price */}
                            <div className="sql-info-column">
                              <div className="sql-info-label">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="14" 
                                  height="14" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  className="sql-info-icon"
                                  aria-hidden="true"
                                >
                                  <line x1="12" x2="12" y1="2" y2="22"></line>
                                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                                </svg>
                                Unit Price
                              </div>
                              {quotation.products && quotation.products.length > 0 ? (
                                <div className="sql-info-value">{formatCurrency(quotation.products[0].unitPrice, quotation.currency, quotation.currencySymbol)}</div>
                              ) : (
                                <div className="sql-info-value">-</div>
                              )}
                            </div>

                            <div className="sql-info-column">
                              <div className="sql-info-label">
                                <CalendarOutlined className="sql-info-icon" />
                                Valid Until
                              </div>
                              <div className="sql-info-value">{quotation.validUntil}</div>
                            </div>

                            <div className="sql-info-column sql-amount-column">
                              <div className="sql-info-label">
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="14" 
                                  height="14" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  className="sql-info-icon"
                                  aria-hidden="true"
                                >
                                  <line x1="12" x2="12" y1="2" y2="22"></line>
                                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                                </svg>
                                Total Amount
                              </div>
                              <div className="sql-info-value sql-amount-value">
                                {formatCurrency(quotation.totalAmount, quotation.currency, quotation.currencySymbol)}
                              </div>
                            </div>

                            {/* View Bulk Pricing Button */}
                            <div className="sql-info-column sql-bulk-pricing-column">
                              <div 
                                className="sql-bulk-pricing-wrapper"
                                onMouseEnter={() => setHoveredQuotationId(quotation.id)}
                                onMouseLeave={() => setHoveredQuotationId(null)}
                              >
                                <button 
                                  className="sql-view-bulk-pricing-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Handle view bulk pricing action
                                  }}
                                >
                                  View Bulk Pricing Tiers
                                </button>
                                
                                {hoveredQuotationId === quotation.id && (
                                  <div className="sql-bulk-pricing-popup">
                                    <div className="sql-popup-header">
                                      <h4>Bulk Pricing Tiers</h4>
                                    </div>
                                    <div className="sql-popup-content">
                                      <table className="sql-pricing-table">
                                        <thead>
                                          <tr>
                                            <th>Quantity Range</th>
                                            <th>Unit Price</th>
                                            <th>Discount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {getBulkPricingData(quotation).length > 0 ? (
                                            getBulkPricingData(quotation).map((tier, index) => (
                                            <tr key={index}>
                                              <td>
                                                {tier.minQty} {tier.maxQty ? `- ${tier.maxQty}` : '+'} units
                                              </td>
                                              <td className="sql-price-cell">
                                                  {formatCurrency(tier.price, quotation.currency, quotation.currencySymbol)}
                                              </td>
                                              <td className="sql-discount-cell">
                                                {tier.discount}
                                                </td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan="3" style={{ textAlign: 'center', padding: '10px', color: '#999' }}>
                                                No bulk pricing available
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* View Details and Quality Inspector Button - Only for Accepted status */}
                            {quotation.status === 'Accepted' && (
                              <div className="sql-info-column sql-acceptance-details-column">
                                <div className="sql-acceptance-details-wrapper" style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                  <button 
                                    className="sql-view-acceptance-details-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAcceptQuotation(quotation);
                                      setShowAcceptanceDetailsModal(true);
                                    }}
                                  >
                                    View Details
                                  </button>
                                  {/* Draft → Submit Request; submitted QA request → no button; else → Quality Inspector Evaluation */}
                                  {(() => {
                                    const quotationId = Number(quotation.quotation_id);
                                    const hasDraft = qaRequestDrafts[quotationId];
                                    const hasSubmitted = submittedQARequests[quotationId];
                                    if (hasSubmitted) return null; // Already submitted – don't show button
                                    if (hasDraft) {
                                      return (
                                        <button 
                                          className="sql-view-acceptance-details-btn"
                                          style={{ 
                                            backgroundColor: '#ff6b35', 
                                            borderColor: '#ff6b35',
                                            color: '#fff',
                                            marginTop: '8px'
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubmitQARequest(quotation);
                                          }}
                                        >
                                          Submit Request
                                        </button>
                                      );
                                    }
                                    return (
                                      <button 
                                        className="sql-view-acceptance-details-btn"
                                        style={{ 
                                          backgroundColor: '#1a237e', 
                                          borderColor: '#1a237e',
                                          color: '#fff',
                                          marginTop: '8px'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedQuotationForQA(quotation);
                                          setShowQualityInspectorModal(true);
                                        }}
                                      >
                                        Quality Inspector Evaluation
                                      </button>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}

                            {/* View Reason Button - Only for Rejected status */}
                            {quotation.status === 'Rejected' && (
                              <div className="sql-info-column sql-reject-reason-column">
                                <div className="sql-reject-reason-wrapper">
                                  <button 
                                    className="sql-view-reject-reason-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedRejectQuotation(quotation);
                                      setShowRejectReasonModal(true);
                                    }}
                                  >
                                    View Reason
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="sql-card-arrow" onClick={() => navigate('/seller-quotation-accept', { state: { quotation: quotation.quotationData, status: quotation.status } })}>
                          <svg 
                            viewBox="64 64 896 896" 
                            focusable="false" 
                            data-icon="right" 
                            width="12" 
                            height="12" 
                            fill="currentColor" 
                            aria-hidden="true"
                            className="sql-arrow-icon"
                          >
                            <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
                          </svg>
                        </div>
                      </div>
                    ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : loading ? (
            <div className="sql-empty-state">
              <p>Loading quotations...</p>
            </div>
          ) : (
            <div className="sql-empty-state">
              <p>No quotations found in this category.</p>
            </div>
          )}
        </div>
      </div>

      {/* Reject Reason Modal */}
      {showRejectReasonModal && selectedRejectQuotation && (
        <div className="sql-modal-overlay" onClick={() => setShowRejectReasonModal(false)}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Reject Reason</h2>
                <p className="brfq-view-request-subtitle">View rejection details for this quotation</p>
              </div>
              <button className="brfq-view-request-close" onClick={() => setShowRejectReasonModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="brfq-view-request-body">
              {(() => {
                // Get reject reason from transformed data or quotationData
                const rejectReason = selectedRejectQuotation.rejectReason || 
                                   selectedRejectQuotation.quotationData?.reject_reason || 
                                   selectedRejectQuotation.quotationData?.rejection_reason || 
                                   null;
                
                // Get reject remarks from transformed data or quotationData
                const rejectRemarks = selectedRejectQuotation.rejectRemarks || 
                                    selectedRejectQuotation.quotationData?.rejection_remarks || 
                                    null;
                
                // Debug logging
                console.log('🔍 Reject Reason Modal Data:', {
                  selectedRejectQuotation,
                  rejectReason,
                  rejectRemarks,
                  quotationData: selectedRejectQuotation.quotationData
                });
                
                return (
                  <>
                    {rejectReason && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <path d="M14 2v6h6"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                            <path d="M10 9H8"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Reason</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description">
                              {rejectReason === 'price-too-high' ? 'Price too high' :
                               rejectReason === 'quality-issues' ? 'Quality issues' :
                               rejectReason === 'delivery-time' ? 'Delivery time not acceptable' :
                               rejectReason === 'terms-not-suitable' ? 'Terms not suitable' :
                               rejectReason === 'other' ? 'Other' :
                               rejectReason}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {rejectRemarks && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Comment</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description">{rejectRemarks}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {!rejectReason && !rejectRemarks && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description" style={{ color: '#999', fontStyle: 'italic' }}>
                              No rejection details available for this quotation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {selectedRejectQuotation.quotationData?.rejection_attachment && (
                <div className="brfq-view-request-section">
                  <div className="brfq-view-request-section-header">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                      <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                    </svg>
                    <h3 className="brfq-view-request-section-title">Attachments</h3>
                  </div>
                  <div className="brfq-view-request-grid">
                    <div className="brfq-view-request-field brfq-view-request-field-full">
                      <div className="brfq-reject-attachments-list">
                        <div className="brfq-reject-attachment-item">
                          <div className="brfq-attachment-left">
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="20" 
                              height="20" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                              className="brfq-attachment-icon"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                              <path d="M10 9H8"></path>
                              <path d="M16 13H8"></path>
                              <path d="M16 17H8"></path>
                            </svg>
                            <div className="brfq-attachment-info">
                              <div className="brfq-attachment-name">
                                {selectedRejectQuotation.quotationData.rejection_attachment.split('/').pop() || 'Rejection Attachment'}
                              </div>
                              <div className="brfq-attachment-details">
                                <span>{selectedRejectQuotation.quotationData.rejection_attachment.split('.').pop()?.toUpperCase() || 'FILE'}</span>
                                <span className="brfq-attachment-separator">•</span>
                                <span>Attachment</span>
                              </div>
                            </div>
                          </div>
                          <div className="brfq-attachment-actions">
                            <button 
                              className="brfq-attachment-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                const baseUrl = 'http://localhost:3000';
                                const attachmentPath = selectedRejectQuotation.quotationData.rejection_attachment;
                                const fileUrl = attachmentPath.startsWith('http') ? attachmentPath : `${baseUrl}${attachmentPath.startsWith('/') ? attachmentPath : '/' + attachmentPath}`;
                                window.open(fileUrl, '_blank');
                              }}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              View
                            </button>
                            <button 
                              className="brfq-attachment-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                const baseUrl = 'http://localhost:3000';
                                const attachmentPath = selectedRejectQuotation.quotationData.rejection_attachment;
                                const fileUrl = attachmentPath.startsWith('http') ? attachmentPath : `${baseUrl}${attachmentPath.startsWith('/') ? attachmentPath : '/' + attachmentPath}`;
                                const link = document.createElement('a');
                                link.href = fileUrl;
                                link.download = selectedRejectQuotation.quotationData.rejection_attachment.split('/').pop() || 'download';
                                link.target = '_blank';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" x2="12" y1="15" y2="3"></line>
                              </svg>
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Acceptance Details Modal */}
      {showAcceptanceDetailsModal && selectedAcceptQuotation && (
        <div className="sql-modal-overlay" onClick={() => setShowAcceptanceDetailsModal(false)}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Acceptance Details</h2>
                <p className="brfq-view-request-subtitle">View acceptance details for this quotation</p>
              </div>
              <button className="brfq-view-request-close" onClick={() => setShowAcceptanceDetailsModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="brfq-view-request-body">
              {(() => {
                // Get acceptance notes from transformed data or quotationData
                const acceptanceNotes = selectedAcceptQuotation.acceptanceNotes || 
                                     selectedAcceptQuotation.quotationData?.acceptance_notes || 
                                     null;
                
                // Get acceptance attachment from transformed data or quotationData
                const acceptanceAttachment = selectedAcceptQuotation.acceptanceAttachment || 
                                           selectedAcceptQuotation.quotationData?.acceptance_attachment || 
                                           null;
                
                return (
                  <>
                    {acceptanceNotes && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Comment</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description">{acceptanceNotes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {acceptanceAttachment && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-section-header">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="brfq-view-request-section-icon">
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                          </svg>
                          <h3 className="brfq-view-request-section-title">Attachments</h3>
                        </div>
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <div className="brfq-reject-attachments-list">
                              <div className="brfq-reject-attachment-item">
                                <div className="brfq-attachment-left">
                                  <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className="brfq-attachment-icon"
                                  >
                                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                                    <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                                    <path d="M10 9H8"></path>
                                    <path d="M16 13H8"></path>
                                    <path d="M16 17H8"></path>
                                  </svg>
                                  <div className="brfq-attachment-info">
                                    <div className="brfq-attachment-name">
                                      {acceptanceAttachment.split('/').pop() || 'Acceptance Attachment'}
                                    </div>
                                    <div className="brfq-attachment-details">
                                      <span>{acceptanceAttachment.split('.').pop()?.toUpperCase() || 'FILE'}</span>
                                      <span className="brfq-attachment-separator">•</span>
                                      <span>Attachment</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="brfq-attachment-actions">
                                  <button 
                                    className="brfq-attachment-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const baseUrl = 'http://localhost:3000';
                                      const fileUrl = acceptanceAttachment.startsWith('http') ? acceptanceAttachment : `${baseUrl}${acceptanceAttachment.startsWith('/') ? acceptanceAttachment : '/' + acceptanceAttachment}`;
                                      window.open(fileUrl, '_blank');
                                    }}
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                      <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                    View
                                  </button>
                                  <button 
                                    className="brfq-attachment-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const baseUrl = 'http://localhost:3000';
                                      const fileUrl = acceptanceAttachment.startsWith('http') ? acceptanceAttachment : `${baseUrl}${acceptanceAttachment.startsWith('/') ? acceptanceAttachment : '/' + acceptanceAttachment}`;
                                      const link = document.createElement('a');
                                      link.href = fileUrl;
                                      link.download = acceptanceAttachment.split('/').pop() || 'download';
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      width="16" 
                                      height="16" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                      <polyline points="7 10 12 15 17 10"></polyline>
                                      <line x1="12" x2="12" y1="15" y2="3"></line>
                                    </svg>
                                    Download
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!acceptanceNotes && !acceptanceAttachment && (
                      <div className="brfq-view-request-section">
                        <div className="brfq-view-request-grid">
                          <div className="brfq-view-request-field brfq-view-request-field-full">
                            <p className="brfq-view-request-description" style={{ color: '#999', fontStyle: 'italic' }}>
                              No acceptance details available for this quotation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Quality Inspector Evaluation Modal */}
      {showQualityInspectorModal && (
        <div className="sql-modal-overlay" onClick={() => setShowQualityInspectorModal(false)}>
          <div className="brfq-view-request-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="brfq-view-request-header">
              <div>
                <h2 className="brfq-view-request-title">Quality Inspector Evaluation</h2>
                <p className="brfq-view-request-subtitle">Do you want to evaluate using a Quality Inspector?</p>
              </div>
              <button className="brfq-view-request-close" onClick={() => setShowQualityInspectorModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="brfq-view-request-body">
              <div style={{ padding: '20px 0', textAlign: 'center' }}>
                <p style={{ fontSize: '16px', color: '#333', marginBottom: '20px' }}>
                  Would you like to request a Quality Inspector to evaluate this accepted quotation?
                </p>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="sql-view-acceptance-details-btn"
                style={{ 
                  backgroundColor: '#fff', 
                  borderColor: '#D1D5DB',
                  color: '#333',
                  border: '1px solid #D1D5DB'
                }}
                onClick={() => {
                  setShowQualityInspectorModal(false);
                }}
              >
                No
              </button>
              <button 
                className="sql-view-acceptance-details-btn"
                style={{ 
                  backgroundColor: '#1a237e', 
                  borderColor: '#1a237e',
                  color: '#fff'
                }}
                onClick={() => {
                  setShowQualityInspectorModal(false);
                  // Navigate to Buyer Request QA page with quotation_id and rfq_id
                  const quotation = selectedQuotationForQA;
                  // Get quotation_id and rfq_id from quotationData (original API data) or directly from quotation
                  const quotationId = quotation?.quotationData?.quotation_id || quotation?.quotation_id;
                  const rfqId = quotation?.quotationData?.rfq_id || quotation?.rfq_id;
                  
                  if (quotation && quotationId && rfqId) {
                    navigate(`/buyer-qa-request?quotation_id=${quotationId}&rfq_id=${rfqId}`, { replace: false });
                  } else {
                    console.error('Missing quotation or RFQ information:', {
                      quotation,
                      quotationId,
                      rfqId,
                      quotationData: quotation?.quotationData
                    });
                    toast.error('Missing quotation or RFQ information');
                  }
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
